import Program from 'nanogl/program'
import Texture2D from 'nanogl/texture-2d'
import { GLContext } from 'nanogl/types'
import Chunk from 'nanogl-pbr/Chunk'
import ChunksSlots from 'nanogl-pbr/ChunksSlots'
import Flag from 'nanogl-pbr/Flag'
import Input, { ShaderType, Uniform } from 'nanogl-pbr/Input'
import TexCoord from 'nanogl-pbr/TexCoord'

import { HexToNewVec3 } from '@/webgl/core/Color'

export default class ScrollingTexture extends Chunk {
  public timeUniform: Uniform
  public tintUniform: Uniform
  public extraUniform: Uniform

  private time: Input
  private uvs: Input
  private tint: Input
  private mainTexture: Texture2D
  private extraTexture: Texture2D

  public extra: Flag
  private extraSettings: Input
  public get ExtraEnabled(): boolean {
    return this.extra.value
  }

  constructor(readonly gl: GLContext) {
    super(true, true)
    this.addChild((this.time = new Input('ScrollTime', 1, ShaderType.FRAGMENT)))
    this.addChild((this.uvs = new Input('TexCoord', 2, ShaderType.FRAGMENT)))
    this.addChild((this.extra = new Flag('extra', false)))
    this.addChild((this.extraSettings = new Input('ExtraSettings', 2, ShaderType.FRAGMENT)))
    this.addChild((this.tint = new Input('Tint', 3, ShaderType.FRAGMENT)))

    this.uvs.attachAttribute(TexCoord.create('aTexCoord0').attrib)
    this.timeUniform = this.time.attachUniform('uScrollTime', 1)
    this.tintUniform = this.tint.attachUniform('uTint', 3)

    this.mainTexture = new Texture2D(this.gl, this.gl.RGBA)
    this.mainTexture.fromData(1, 1, new Uint8Array([1, 1, 1, 0]))

    this.extraTexture = new Texture2D(this.gl, this.gl.RGBA)
    this.extraTexture.fromData(1, 1, new Uint8Array([1, 1, 1, 0]))
  }

  setMainTex(texture: Texture2D): void {
    this.mainTexture = texture
  }

  setTintColor(color: number) {
    this.tintUniform.set(...HexToNewVec3(color))
  }

  setExtraTex(texture: Texture2D): Uniform {
    this.extraTexture = texture
    this.extraUniform = this.extraSettings.attachUniform('uExtraTexture', 3)
    this.extraUniform.value[0] = 0.1
    this.extraUniform.value[1] = 0
    this.extra.enable()
    return this.extraUniform
  }

  setExtraReveal(reveal: number): void {
    if (!this.ExtraEnabled) return
    this.extraUniform.value[1] = reveal
  }

  setup(prg: Program): void {
    prg.tScrollingTexture(this.mainTexture)
    if (this.ExtraEnabled) {
      prg.tExtraTexture(this.extraTexture)
    }
  }

  _genCode(slots: ChunksSlots): void {
    slots.add(
      'pv',
      /*glsl*/ `
      #if extra
      OUT float vExtraOffset;
      float rand(vec3 co){
        return fract(sin(dot(co.xyz ,vec3(12.9898,78.233,45.5432))) * 43758.5453);
      }
      #endif
      `
    )

    slots.add(
      'postv',
      /*glsl*/ `
      #if extra
      // vWorldPosition = vertex.worldMatrix[11][12][13];
      vExtraOffset = rand(vertex.worldMatrix[3].xyz) * 25.0;
      #endif
      `
    )

    slots.add(
      'pf',
      /*glsl*/ `
      uniform sampler2D tScrollingTexture;
      #if extra
      IN float vExtraOffset;
      uniform sampler2D tExtraTexture;
      #define ExtraScale ExtraSettings().x
      #define ExtraReveal ExtraSettings().y
      #endif
    `
    )

    slots.add(
      'postf',
      /*glsl*/ `
      
      vec2 uv = TexCoord();
      uv.y *= 0.2;
      uv += vec2(0.0, ScrollTime() * 0.2);
      vec4 detail = texture2D(tScrollingTexture, uv);
      vec3 color = mix(FragColor.rgb, Tint(), detail.g);

      #if extra
      vec2 uvExtra = TexCoord();
      uvExtra.y += ScrollTime() + vExtraOffset;
      uvExtra.y *= ExtraScale;
      vec4 extraColor = texture2D(tExtraTexture, uvExtra);
      color = mix(color, extraColor.rgb, extraColor.a * ExtraReveal);
      #endif

      FragColor.rgb = color;


      `
    )
  }
}
